'use client';

import { IFile, ReviewListView } from '@sapientpro/sapientpro-data-models';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import styles from './Video.module.scss';
import CustomCursorWrapper from '../CustomCursor/CustomCursorWrapper';
import Img from '../Img';
import PlayButton from './PlayButton';
import useInViewport from '../../useInViewport';
import Button, { ButtonVariants } from '../Button';
import VideoControlsOverlay from '../VideoControlsOverlay/VideoControlsOverlay';

const checkIsRegularVideo = (url: string): boolean => /\.(mp4|webm|ogg)$/i.test(url);

const formatDuration = (durationString: string) => {
  let result = durationString;

  result = result.replace(/PT|S/g, '');
  result = result.replace(/H|M/g, ':');
  result = result.replace(/\b(\d)\b/g, '0$1');

  if (result.length === 2) result = `00:${result}`;

  return result;
};

export type VideoDataProps = {
  title?: string,
  clip?: IFile,
  loop?: boolean,
  autoPlay?: boolean,
  preview?: {
    url: string,
    alternativeText: string,
  },
  controlsOverlay?: boolean;
};

type VideoProps = {
  video: VideoDataProps,
  view: ReviewListView,
  activeIndex: number,
  className?: string,
  videoClassName?: string,
  videoWrapperClassName?: string,
  videoPreviewClassName?: string,
  videoType?: 'default' | 'custom',
  playMode?: 'hover' | 'click' | 'in-view',
  description?: string,
  duration?: string,
  isUnMuted?: boolean,
  withControls?: boolean,
};

const Video = ({
  className,
  videoClassName,
  videoWrapperClassName,
  videoPreviewClassName,
  video,
  view,
  activeIndex,
  videoType = 'default',
  playMode = 'hover',
  description,
  duration,
  isUnMuted = false,
  withControls,
}: VideoProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
  const [isFirstPlay, setIsFirstPlay] = useState<boolean>(true);
  const [isHovered, setIsHovered] = useState<boolean>(true);
  const [isMuted, setIsMuted] = useState<boolean>(!isUnMuted);
  const [hasAudio, setHasAudio] = useState<boolean>(false);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const isVisible = useInViewport(videoWrapperRef);

  function controlVideo(controlFunction: string) {
    if (iframeRef) {
      iframeRef.current?.contentWindow?.postMessage(
        `{"event":"command","func":"${controlFunction}","args":""}`,
        '*',
      );
    }
  }

  const checkAudioAvailability = () => {
    if (videoRef.current && !hasAudio) {
      const videoElement = videoRef.current as any;

      const hasAudioProperty = videoElement.mozHasAudio || videoElement.webkitAudioDecodedByteCount || videoElement.audioTracks?.length;
      setHasAudio(!!hasAudioProperty);
    }
  };

  useEffect(() => {
    if ((videoRef.current as any)?.webkitAudioDecodedByteCount) checkAudioAvailability();
  }, [(videoRef.current as any)?.webkitAudioDecodedByteCount]);

  const onStopVideoClick = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsVideoPlaying(false);
    }
  };

  const onPlayVideoClick = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsVideoPlaying(true);
      checkAudioAvailability();
    }
  };

  const onVideoOverlayHover = () => {
    if (videoRef.current && isFirstPlay) {
      videoRef.current.play();
      setIsVideoPlaying(true);
      setIsFirstPlay(false);
      checkAudioAvailability();
    }
  };

  const onVideoEnd = () => {
    setIsVideoPlaying(false);
    setIsFirstPlay(true);
  };

  const handleMouseMove = () => {
    setIsHovered(true);
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setIsHovered(false), 2000);
  };

  const toggleMuteState = () => {
    setIsMuted(!isMuted);
  };

  useEffect(() => () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
  }, []);

  useEffect(
    () => {
      if (playMode === 'in-view') {
        isVisible ? onPlayVideoClick() : onStopVideoClick();
      }
    },
    [playMode, isVisible],
  );

  useEffect(() => {
    controlVideo('stopVideo');
    setShowVideo(false);
  }, [activeIndex]);

  useEffect(() => {
    checkAudioAvailability();

    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [videoRef.current]);

  const showPreview = !showVideo && video?.preview?.url;
  const isRegularVideo = video.clip ? checkIsRegularVideo(video.clip.url) : false;
  const showMuteButton = hasAudio && !withControls;

  return (
    <div
      ref={videoWrapperRef}
      className={classNames(styles.videoWrapper, styles[`${view}-view`], className)}
    >
      {videoType === 'default' && showPreview && (
        <button
          type='button'
          onClick={() => {
            !!video.clip?.url && setShowVideo(true);
          }}
          className={classNames(styles.previewButton, {
            [styles.previewButton__disabled]: !video.clip?.url,
          })}
        >
          <CustomCursorWrapper
            cursorText='Play'
            view='light'
            className={styles.previewWrapper}
            disabled={!video.clip?.url}
          >
            {!!video?.preview && (
            <Img
              src={video?.preview?.url}
              alt={video?.preview?.alternativeText || 'review video preview'}
              className={classNames(styles.preview, videoPreviewClassName)}
            />
            )}
            {!!video.clip?.url && (
              <div className={styles.previewOverlay}>
                <img
                  src='media/play-icon.svg'
                  alt='Play icon'
                  className={styles.playIcon}
                />
                {!!duration && <span className={styles.videoDuration}>{formatDuration(duration)}</span>}
              </div>
            )}
          </CustomCursorWrapper>
        </button>
      )}
      {(videoType === 'custom' || showVideo) && (
        <div
          className={classNames(styles.video, videoWrapperClassName)}
        >
          {isRegularVideo ? (
            <div
              className={styles.videoContent}
              onMouseMove={handleMouseMove}
            >
              {video.clip && (
                <video
                  ref={videoRef}
                  loop={video.loop}
                  autoPlay={video.autoPlay}
                  muted={isMuted}
                  playsInline
                  className={classNames(styles.videoContent__video, videoClassName)}
                  onEnded={onVideoEnd}
                  preload='none'
                >
                  <source
                    src={video.clip.url}
                  />
                  <track kind='captions' />
                </video>
              )}
              {(!video.autoPlay || video.controlsOverlay) && (
                <div
                  role='presentation'
                  className={classNames(styles.videoContent__overlay, {
                    [styles.videoContent__overlay_playing]: isVideoPlaying,
                    [styles.videoContent__overlay_secondPlay]: !isFirstPlay,
                    [styles.videoContent__overlay_notHovered]: !isHovered,
                  })}
                  {...(playMode === 'hover' ? {
                    onMouseOver: () => !isVideoPlaying && onVideoOverlayHover(),
                    onFocus: () => () => !isVideoPlaying && onVideoOverlayHover(),
                  } : {})}
                >
                  {video.clip && (
                    <>
                      {!withControls && (
                      <PlayButton
                        isPlaying={isVideoPlaying}
                        onClick={isVideoPlaying ? onStopVideoClick : onPlayVideoClick}
                        playButtonClassName={styles.videoContent__overlayPlayButton}
                      />
                      )}
                      {showMuteButton && (
                      <Button
                        variant={ButtonVariants.TRANSPARENT}
                        onClick={toggleMuteState}
                        className={styles.muteButton}
                        ariaLabel={`${isMuted ? 'unmute' : 'mute'} button`}
                      >
                        {isMuted ? (
                          <svg>
                            <use
                              href='/media/unmute-icon.svg#unmuteIconSVG'
                              xlinkHref='/media/unmute-icon.svg#unmuteIconSVG'
                            />
                          </svg>
                        ) : (
                          <svg>
                            <use
                              href='/media/mute-icon.svg#muteIconSVG'
                              xlinkHref='/media/mute-icon.svg#muteIconSVG'
                            />
                          </svg>
                        )}
                      </Button>
                      )}
                    </>
                  )}
                  {!!duration && !isVideoPlaying && <span className={styles.videoContent__overlayVideoDuration}>{duration}</span>}
                </div>
              )}
              {withControls && (
              <VideoControlsOverlay
                toggleMute={toggleMuteState}
                togglePlay={isVideoPlaying ? onStopVideoClick : onPlayVideoClick}
                toggleSpeed={() => null}
                isMuted={isMuted}
                isPlaying={isVideoPlaying}
                isSpeededUp={false}
                videoRef={videoRef}
              />
              )}
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              src={`${video.clip?.url}?rel=0&enablejsapi=1`}
              title={video.title}
              frameBorder='0'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
              allowFullScreen
              onLoad={() => controlVideo('playVideo')}
            />
          )}
        </div>
      )}
      {!!description && (
      <div className={styles.videoDescription}>
        <svg className={styles.descriptionIcon}>
          <use
            href='/media/play_icon_small.svg#play-icon-small'
            xlinkHref='/media/play_icon_small.svg#play-icon-small'
          />
        </svg>
        <p className={classNames(styles.descriptionText, 'bodySmall')}>Short video description</p>
      </div>
      )}
    </div>
  );
};

export default React.memo(Video);
